import './utils/reset-styles.css'
import '@fontsource/roboto/300.css'
import '@fontsource/roboto/400.css'
import '@fontsource/roboto/500.css'
import '@fontsource/roboto/700.css'

import { BrowserRouter as Router, Routes, Route } from 'react-router-dom'
import AuthProvider from 'react-auth-kit/AuthProvider'
import createStore from 'react-auth-kit/createStore'
import RequireAuth from '@auth-kit/react-router/RequireAuth'
import { createTheme } from '@mui/material'
import { ThemeProvider } from '@mui/material/styles'
import { SnackbarProvider } from 'notistack'

import { NotificationProvider } from './contexts/NotificationContext'
import { LocaleProvider } from './contexts/LocaleContext'

import Onboarding from './pages/Onboarding/Onboarding'
import { RedirectOptions } from './pages/RedirectOptions'
import { UserDashboard } from './pages/UserDashboard'
import { Login } from './pages/Login'
import { Intake } from './pages/Intake'
import { Dashboard } from './pages/Dashboard'
import { Notifications } from './pages/Notifications'
import { SystemEvents } from './pages/SystemEvents'
import { ReportOptimizeCounterMeasure } from './pages/ReportOptimizeCounterMeasure'
import { ScheduleImport } from './pages/ScheduleImport'
import { UserImport } from './pages/UserImport'

import { ROUTES } from './routes'
import { PLANS } from './utils/planNames'
import { ROLES } from './utils/roles'
import { ConfigProvider } from './contexts/ConfigContext'
import { NavbarProvider } from './contexts/NavbarContext'
import { ProtectedRoute } from './components'
import { Logout } from './pages/Logout'
import { PVTWrapper } from './pages/PVT'
import { Sleep } from './pages/Sleep'
import { Caffeine } from './pages/Caffeine'
import { AccountConfig } from './pages/AccountConfig'
import { IntakeList } from './pages/IntakeList'
import { PredictionHistory } from './pages/PredictionHistory'
import { ShiftFatigue } from './pages/ShiftFatigue'
import { ReportPrediction } from './pages/ReportPrediction'
import { ReportFatigueEvolution } from './pages/ReportFatigueEvolution'
import { ReportSleepQuantity } from './pages/ReportSleepQuantity'
import { ReportDistributionFatigue } from './pages/ReportDistributionFatigue'
import { AdminCockpit } from './pages/AdminCockpit'
import { UserFullList } from './pages/UserFullList'
import { ReportUserDataQuality } from './pages/ReportUserDataQuality'
import { DataExport } from './pages/DataExport'
import { ShiftList } from './pages/ShiftList'
import { Interventions } from './pages/Interventions'
import { AccountChange } from './pages/AccountChange'


const theme = createTheme({
  palette: {
    primary: {
      main: '#e27837',
    },
    success: {
      main: '#00B69B',
    },
    error: {
      main: '#BE3756',
    },
  },
})

const store = createStore({
  authName: '_auth',
  authType: 'cookie',
  cookieDomain: window.location.hostname,
  cookieSecure: window.location.protocol === 'https:',
})

export const getRoutes = () => {
  return [
    {
      path: ROUTES.REDIRECT_OPTIONS,
      View: RedirectOptions,
      exact: true,
      restrictions: {},
    },
    {
      path: ROUTES.ONBOARDING,
      View: Onboarding,
      exact: true,
      restrictions: {},
    },
    {
      path: ROUTES.INTAKE,
      View: Intake,
      exact: true,
      restrictions: {},
    },
    {
      path: ROUTES.USER_DASHBOARD,
      View: UserDashboard,
      exact: true,
      restrictions: {},
    },
    {
      path: ROUTES.DASHBOARD,
      View: Dashboard,
      exact: true,
      restrictions: {
        roles: [ROLES.SUPERVISOR, ROLES.ADMINISTRATOR, ROLES.SYSTEM_ADMIN],
      },
    },
    {
      path: ROUTES.NOTIFICATIONS,
      View: Notifications,
      exact: true,
      restrictions: {},
    },
    {
      path: ROUTES.INTERVENTIONS,
      View: Interventions,
      exact: true,
      restrictions: {},
    },
    {
      path: ROUTES.SYSTEM_EVENTS,
      View: SystemEvents,
      exact: true,
      restrictions: {
        roles: [ROLES.SUPERVISOR, ROLES.ADMINISTRATOR, ROLES.SYSTEM_ADMIN],
      },
    },
    {
      path: ROUTES.PVT,
      View: PVTWrapper,
      exact: true,
      restrictions: {},
    },
    {
      path: ROUTES.SLEEP,
      View: Sleep,
      exact: true,
      restrictions: {},
    },
    {
      path: ROUTES.CAFFEINE,
      View: Caffeine,
      exact: true,
      restrictions: {},
    },
    {
      path: ROUTES.SCHEDULE_IMPORT,
      View: ScheduleImport,
      exact: true,
      restrictions: {
        roles: [ROLES.ADMINISTRATOR, ROLES.SYSTEM_ADMIN],
      },
    },
    {
      path: ROUTES.USER_IMPORT,
      View: UserImport,
      exact: true,
      restrictions: {
        roles: [ROLES.ADMINISTRATOR, ROLES.SYSTEM_ADMIN],
      },
    },
    {
      path: ROUTES.ACCOUNT_CONFIG,
      View: AccountConfig,
      exact: true,
      restrictions: {
        roles: [ROLES.ADMINISTRATOR, ROLES.SYSTEM_ADMIN],
      },
    },
    {
      path: ROUTES.DATA_EXPORT,
      View: DataExport,
      exact: true,
      restrictions: {
        roles: [ROLES.ADMINISTRATOR, ROLES.SYSTEM_ADMIN],
      },
    },
    {
      path: ROUTES.INTAKE_LIST,
      View: IntakeList,
      exact: true,
      restrictions: {},
    },
    {
      path: ROUTES.OPTIMIZE_REPORT,
      View: ReportOptimizeCounterMeasure,
      exact: true,
      restrictions: {
        roles: [ROLES.SUPERVISOR, ROLES.ADMINISTRATOR, ROLES.SYSTEM_ADMIN],
        plans: [PLANS.OPTIMIZED],
      },
    },
    {
      path: ROUTES.SHIFT_FATIGUE,
      View: ShiftFatigue,
      exact: true,
      restrictions: {
        roles: [ROLES.ADMINISTRATOR, ROLES.SYSTEM_ADMIN, ROLES.SUPERVISOR],
        plans: [PLANS.OPTIMIZED],
      },
    },
    {
      path: ROUTES.PREDICTION,
      View: ReportPrediction,
      exact: true,
      restrictions: {
        roles: [ROLES.ADMINISTRATOR, ROLES.SYSTEM_ADMIN, ROLES.SUPERVISOR],
        plans: [PLANS.OPTIMIZED, PLANS.PREDICTION, PLANS.NOW],
      },
    },
    {
      path: ROUTES.PREDICTION_HISTORY,
      View: PredictionHistory,
      exact: true,
      restrictions: {
        roles: [ROLES.ADMINISTRATOR, ROLES.SYSTEM_ADMIN, ROLES.SUPERVISOR],
        plans: [PLANS.OPTIMIZED, PLANS.PREDICTION, PLANS.NOW],
      },
    },
    {
      path: ROUTES.FATIGUE_EVOLUTION,
      View: ReportFatigueEvolution,
      exact: true,
      restrictions: {
        roles: [ROLES.ADMINISTRATOR, ROLES.SYSTEM_ADMIN, ROLES.SUPERVISOR],
        plans: [PLANS.OPTIMIZED, PLANS.PREDICTION, PLANS.NOW],
      },
    },
    {
      path: ROUTES.FATIGUE_DISTRIBUTION,
      View: ReportDistributionFatigue,
      exact: true,
      restrictions: {
        roles: [ROLES.ADMINISTRATOR, ROLES.SYSTEM_ADMIN, ROLES.SUPERVISOR],
        plans: [PLANS.OPTIMIZED, PLANS.PREDICTION, PLANS.NOW],
      },
    },
    {
      path: ROUTES.SLEEP_QUANTITY,
      View: ReportSleepQuantity,
      exact: true,
      restrictions: {
        roles: [ROLES.ADMINISTRATOR, ROLES.SYSTEM_ADMIN, ROLES.SUPERVISOR],
        plans: [PLANS.OPTIMIZED, PLANS.PREDICTION, PLANS.NOW],
      },
    },
    {
      path: ROUTES.ADMIN_COCKPIT,
      View: AdminCockpit,
      exact: true,
      restrictions: {
        roles: [ROLES.SYSTEM_ADMIN]
      },
    },
    {
      path: ROUTES.USER_FULL_LIST,
      View: UserFullList,
      exact: true,
      restrictions: {
        roles: [ROLES.SYSTEM_ADMIN, ROLES.ADMINISTRATOR]
      },
    },
    {
      path: ROUTES.REPORT_USER_DATA_QUALITY,
      View: ReportUserDataQuality,
      exact: true,
      restrictions: {
        roles: [ROLES.SYSTEM_ADMIN, ROLES.ADMINISTRATOR, ROLES.SUPERVISOR]
      },
    },
    {
      path: ROUTES.SHIFT_LIST,
      View: ShiftList,
      exact: true,
      restrictions: {
        roles: [ROLES.SYSTEM_ADMIN, ROLES.ADMINISTRATOR, ROLES.SUPERVISOR]
      },
    },
    {
      path: ROUTES.ACCOUNT_CHANGE,
      View: AccountChange,
      exact: true,
      restrictions: {},
    },
  ]
}

const AppRoutes = () => {
  return (
    <Router>
      <Routes>
        <Route
          path={ROUTES.LOGIN}
          exact
          element={<Login />}
        />
        <Route
          path={ROUTES.LOGOUT}
          exact
          element={<Logout />}
        />
        {getRoutes().map(({ path, restrictions, View, ...rest }, index) => (
          <Route
            path={path}
            element={
              <RequireAuth fallbackPath={ROUTES.LOGIN}>
                <ProtectedRoute
                  View={View}
                  restrictions={restrictions}
                />
              </RequireAuth>
            }
            key={index}
            {...rest}
          />
        ))}
      </Routes>
    </Router>
  )
}

const App = () => {
  return (
    <AuthProvider store={store}>
      <ThemeProvider theme={theme}>
        <ConfigProvider>
          <LocaleProvider>
            <SnackbarProvider>
              <NotificationProvider>
                <NavbarProvider>
                  <AppRoutes />
                </NavbarProvider>
              </NotificationProvider>
            </SnackbarProvider>
          </LocaleProvider>
        </ConfigProvider>
      </ThemeProvider>
    </AuthProvider>
  )
}

export default App
